import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { STATE_PATHS } from "@/data/mexicoStates";
import { STATE_LABEL } from "@/data/clubs";

const empty = { estado: "", nombre: "", nivel: "", institucion: "", giro: "", email: "", instagram: "" };

const AdminClubs = () => {
  const [clubs, setClubs] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const stateOptions = useMemo(
    () => STATE_PATHS.map((s) => ({ value: s.name, label: STATE_LABEL[s.name] || s.name })).sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  const load = () => api.get("/clubs").then((r) => setClubs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(empty); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    ["nivel", "institucion", "giro", "email", "instagram"].forEach((k) => { if (!payload[k]) payload[k] = null; });
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/clubs/${editingId}`, payload);
        toast.success("Club actualizado");
      } else {
        await api.post("/clubs", payload);
        toast.success("Club agregado");
      }
      reset();
      load();
    } catch { toast.error("No se pudo guardar el club"); }
    finally { setSaving(false); }
  };

  const edit = (c) => {
    setEditingId(c.id);
    setForm({ estado: c.estado || "", nombre: c.nombre || "", nivel: c.nivel || "", institucion: c.institucion || "", giro: c.giro || "", email: c.email || "", instagram: c.instagram || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este club?")) return;
    await api.delete(`/clubs/${id}`);
    toast.success("Club eliminado");
    if (editingId === id) reset();
    load();
  };

  const visible = clubs
    .filter((c) => {
      const q = filter.toLowerCase();
      return !q || (c.nombre || "").toLowerCase().includes(q) || (c.estado || "").toLowerCase().includes(q) || (STATE_LABEL[c.estado] || "").toLowerCase().includes(q);
    })
    .sort((a, b) => (a.estado || "").localeCompare(b.estado || "") || (a.nombre || "").localeCompare(b.nombre || ""));

  const field = (key, label, extra = {}) => (
    <div>
      <label className="text-xs font-semibold text-brand-ink/70">{label}</label>
      <input data-testid={`club-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1 w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm outline-none focus:border-brand-magenta" {...extra} />
    </div>
  );

  return (
    <div className="mt-8">
      <form onSubmit={submit} data-testid="club-form" className="bg-white border border-[#E5E5E5] rounded-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold text-brand-ink">{editingId ? "Editar club" : "Agregar club"}</h2>
          {editingId && (
            <button type="button" onClick={reset} data-testid="club-cancel" className="inline-flex items-center gap-1 text-sm text-brand-ink/60 hover:text-brand-ink"><X size={15} /> Cancelar edición</button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-semibold text-brand-ink/70">Estado</label>
            <select required data-testid="club-estado" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} className="mt-1 w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm outline-none focus:border-brand-magenta bg-white">
              <option value="">Selecciona…</option>
              {stateOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {field("nombre", "Nombre del club", { required: true })}
          {field("nivel", "Nivel (Preparatoria/Universidad)")}
          {field("institucion", "Institución")}
          {field("giro", "Giro / temática")}
          {field("email", "Correo")}
          {field("instagram", "Instagram (URL)")}
        </div>
        <button type="submit" disabled={saving} data-testid="save-club-btn" className="mt-5 inline-flex items-center gap-2 bg-brand-magenta text-white font-semibold px-5 py-2.5 rounded-sm hover:bg-brand-orange transition-colors disabled:opacity-60">
          <Plus size={16} /> {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Agregar club"}
        </button>
      </form>

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-heading text-lg font-bold text-brand-ink">Clubs ({clubs.length})</h2>
        <input data-testid="club-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Buscar por nombre o estado…" className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm outline-none focus:border-brand-magenta w-64 max-w-full" />
      </div>

      <div className="overflow-x-auto border border-[#E5E5E5] rounded-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F6F6F6] text-left text-brand-ink/60">
              <th className="px-3 py-2 font-semibold">Estado</th>
              <th className="px-3 py-2 font-semibold">Club</th>
              <th className="px-3 py-2 font-semibold hidden md:table-cell">Institución</th>
              <th className="px-3 py-2 font-semibold hidden sm:table-cell">Giro</th>
              <th className="px-3 py-2 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => (
              <tr key={c.id} data-testid={`admin-club-${c.id}`} className="border-t border-[#F0F0F0]">
                <td className="px-3 py-2 whitespace-nowrap">{STATE_LABEL[c.estado] || c.estado}</td>
                <td className="px-3 py-2 font-semibold text-brand-ink">{c.nombre}</td>
                <td className="px-3 py-2 text-brand-ink/60 hidden md:table-cell">{c.institucion || "—"}</td>
                <td className="px-3 py-2 text-brand-ink/60 hidden sm:table-cell">{c.giro || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`edit-club-${c.id}`} onClick={() => edit(c)} className="p-1.5 text-brand-ink hover:text-brand-magenta"><Pencil size={15} /></button>
                    <button data-testid={`delete-club-${c.id}`} onClick={() => remove(c.id)} className="p-1.5 text-brand-ink hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-brand-ink/50">Sin resultados.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminClubs;
